import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useActiveAnnotation from '../../hooks/useActiveAnnotation'
import { DraftAnnotation } from '../../types'

import {
  AnnotationOverlayContextStatus,
  AnnotationOverlayContextAvailable,
} from './AnnotationOverlayContext.types'

const UNAVAILABLE_STATE = { status: 'unavailable' } as const
export const Context = React.createContext<AnnotationOverlayContextStatus>(
  UNAVAILABLE_STATE
)

interface AnnotationOverlayContextProps {
  disabled?: boolean
  onDraftAnnotation?: () => void
  onMovingAnnotation?: (movingAnnotation: boolean) => void
  isViewingLatestVersion: boolean
}

const AnnotationOverlayContext: React.FC<AnnotationOverlayContextProps> = props => {
  const {
    children,
    disabled,
    onDraftAnnotation,
    onMovingAnnotation,
    isViewingLatestVersion,
  } = props

  const origin = useRef<HTMLElement | null>(null)
  const scale = useRef<number | null>(null)

  const [
    activeAnnotation /* eslint-disable-line @typescript-eslint/no-unused-vars */,
    setActiveAnnotation,
  ] = useActiveAnnotation()

  const [placeDraftAnnotation, _setPlaceDraftAnnotation] = useState(false)
  const [movingAnnotation, setMovingAnnotation] = useState<string | null>(null)
  const [
    draftAnnotation,
    addDraftAnnotation,
  ] = useState<DraftAnnotation | null>(null)

  const [draftAnnotationComment, setDraftAnnotationComment] = useState<
    string | null
  >(null)

  const setPlaceDraftAnnotation = useCallback((value: boolean) => {
    _setPlaceDraftAnnotation(value)

    // Remove the draft annotation when the add mode is closed
    if (value === false) {
      addDraftAnnotation(null)
      setDraftAnnotationComment(null)
    }
  }, [])

  /**
   * Call the "onDraftAnnotation" when the placeDraftAnnotation is active
   */
  useEffect(() => {
    if (placeDraftAnnotation) {
      onDraftAnnotation?.()
      setActiveAnnotation()
    }
  }, [onDraftAnnotation, placeDraftAnnotation, setActiveAnnotation])

  /**
   * Cache the "onMovingAnnotation" to prevent the function
   * to re-trigger the useEffect
   */
  const onMovingAnnotationRef = useRef(onMovingAnnotation)
  useEffect(() => {
    onMovingAnnotationRef.current = onMovingAnnotation
  }, [onMovingAnnotation])

  /**
   * Call the "onMovingAnnotation" when an annotation is being moved
   */
  useEffect(() => {
    onMovingAnnotationRef.current?.(!!movingAnnotation)
  }, [movingAnnotation])

  const state: AnnotationOverlayContextAvailable = useMemo(
    () => ({
      status: 'available' as const,
      placeDraftAnnotation,
      setPlaceDraftAnnotation,
      isViewingLatestVersion,
      draftAnnotation,
      draftAnnotationComment,
      setDraftAnnotationComment,
      addDraftAnnotation,
      movingAnnotation,
      setMovingAnnotation,
      setActiveAnnotation,
      activeAnnotation,
      origin,
      scale,
    }),
    [
      placeDraftAnnotation,
      draftAnnotation,
      addDraftAnnotation,
      setPlaceDraftAnnotation,
      movingAnnotation,
      setMovingAnnotation,
      setActiveAnnotation,
      activeAnnotation,
      draftAnnotationComment,
      setDraftAnnotationComment,
      isViewingLatestVersion,
    ]
  )

  return (
    <Context.Provider value={disabled ? UNAVAILABLE_STATE : state}>
      {children}
    </Context.Provider>
  )
}

export default AnnotationOverlayContext
